import React, { useState, useEffect, useCallback } from 'react';
import ChatsTab from '../components/sessions/ChatsTab';
import MeetingsTab from '../components/sessions/MeetingsTab';
import { useAuth } from '../context/AuthContext';
import { useBranding, useDomain, useVisualTheme } from '../context/DomainContext';
import {
  Avatar, Box, Button, Card, CardContent, CardHeader, Container,
  Divider, Grid, IconButton, Paper, Stack, TextField, Typography,
  Alert, Skeleton, Tooltip, Tab, Tabs, Badge, Chip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, List, ListItem, ListItemAvatar, ListItemText, ListItemButton,
  CardActions
} from '@mui/material';
import {
  Email as EmailIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Security as SecurityIcon, Person as PersonIcon, Verified as VerifiedIcon,
  Settings as SettingsIcon, PhotoCamera as PhotoCameraIcon, VpnKey as VpnKeyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon, Business as BusinessIcon,
  Cake as CakeIcon, Phone as PhoneIcon,
  SmartToy as SmartToyIcon,
  Style as StyleIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Male as MaleIcon, Female as FemaleIcon, Transgender as TransgenderIcon,
  Add as AddIcon,
  Badge as BadgeIcon,
  MeetingRoom as MeetingRoomIcon,
  Group as GroupIcon,
  Folder as FolderIcon,
  Domain as DomainIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  Collections as CollectionsIcon,
  Chat as ChatIcon,
  VideoCall as VideoCallIcon,
  Forum as ForumIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  PauseCircle as PauseCircleIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { BACKEND_URL } from '../config';

// Componenti esistenti... (AgentSelectionModal, AgentEditModal, ecc.)
const AgentSelectionModal = ({ open, onClose, onSelect, primaryColor, projectId }) => {
  const { user } = useAuth();
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('');

  const fetchAvailableAgents = useCallback(async () => {
    if (!user || !projectId || !open) return;
    
    setLoading(true);
    setError('');
    try {
      const token = await user.getIdToken();
      const url = new URL(`${BACKEND_URL}/api/agents/available`);
      url.searchParams.append('project_id', projectId);
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableAgents(data);
        if (data.length > 0) {
          const firstCategory = data[0].sys_agent_category || 'Senza Categoria';
          setExpandedCategory(firstCategory);
        }
      } else {
        setError('Errore nel caricamento degli agenti disponibili.');
      }
    } catch (error) {
      console.error('Errore nel caricamento agenti disponibili:', error);
      setError('Errore di connessione.');
    } finally {
      setLoading(false);
    }
  }, [user, projectId, open]);

  useEffect(() => {
    fetchAvailableAgents();
  }, [fetchAvailableAgents]);

  const handleSelectAgent = async (agent) => {
    try {
      const token = await user.getIdToken();
      const url = new URL(`${BACKEND_URL}/api/agents/add-from-project`);
      url.searchParams.append('project_id', projectId);
      url.searchParams.append('sys_agent_blueprint_id', agent.sys_agent_blueprint_id);
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        onSelect(agent);
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Errore nell\'aggiunta dell\'agente.');
      }
    } catch (error) {
      console.error('Errore nell\'aggiunta agente:', error);
      setError('Errore di connessione.');
    }
  };

  const groupedByCategory = availableAgents.reduce((groups, agent) => {
    const category = agent.sys_agent_category || 'Senza Categoria';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(agent);
    return groups;
  }, {});

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ color: primaryColor }}>
        <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Aggiungi Agente dal Progetto
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableAgents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SmartToyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nessun agente disponibile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tutti gli agenti abilitati nel progetto sono già stati aggiunti alla tua lista.
            </Typography>
          </Box>
        ) : (
          <Box>
            {Object.entries(groupedByCategory).map(([category, agents]) => (
              <Accordion 
                key={category}
                expanded={expandedCategory === category}
                onChange={() => setExpandedCategory(expandedCategory === category ? '' : category)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CategoryIcon sx={{ color: primaryColor }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: primaryColor }}>
                      {category}
                    </Typography>
                    <Chip 
                      label={agents.length} 
                      size="small" 
                      sx={{ 
                        bgcolor: `${primaryColor}20`, 
                        color: primaryColor,
                        fontWeight: 600 
                      }} 
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List disablePadding>
                    {agents.map((agent) => (
                      <ListItem 
                        key={agent.id}
                        sx={{ 
                          border: 1, 
                          borderColor: 'divider',
                          borderRadius: 1, 
                          mb: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { 
                            borderColor: primaryColor,
                            backgroundColor: `${primaryColor}08`
                          }
                        }}
                        onClick={() => handleSelectAgent(agent)}
                      >
                        <ListItemAvatar sx={{ display: { xs: 'none', sm: 'flex' } }}>
                          <Avatar 
                            src={agent.ux_avatar_url || agent.pro_override_avatar_url} 
                            sx={{ bgcolor: primaryColor }}
                          >
                            {(agent.ux_name || agent.pro_override_display_name || 'A').charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText 
                          primary={
                            <Typography variant="subtitle1" fontWeight={600}>
                              {agent.ux_name || agent.pro_override_display_name || 'Agente Senza Nome'}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {agent.ux_key || `Blueprint: ${agent.sys_agent_blueprint_id?.substring(0, 8)}...`}
                              </Typography>
                              {agent.ux_description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {agent.ux_description}
                                </Typography>
                              )}
                              {agent.ux_default_user_message && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}
                                >
                                  "{agent.ux_default_user_message}"
                                </Typography>
                              )}
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                <Chip 
                                  label={agent.sys_agent_category || 'Generale'} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem' }}
                                />
                                {agent.sys_agent_collection && (
                                  <Chip 
                                    label={agent.sys_agent_collection} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          sx={{ 
                            bgcolor: primaryColor, 
                            '&:hover': { bgcolor: `${primaryColor}dd` },
                            ml: { xs: 1, sm: 2 },
                            minWidth: { xs: 'auto', sm: 'auto' },
                            px: { xs: 1, sm: 2 }
                          }}
                        >
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Aggiungi</Box>
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>+</Box>
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

const AgentEditModal = ({ open, onClose, agent, onSave, visualTheme }) => {
  const [formData, setFormData] = useState({});
  const [roleConflict, setRoleConflict] = useState(null);
  const [pendingSave, setPendingSave] = useState(null);
  const primaryColor = visualTheme?.secondary_colors?.accent || '#1976d2';
  const { user } = useAuth();

  useEffect(() => {
    const initialData = agent ? {
      ...agent
    } : {
      sys_agent_blueprint_id: '',
      user_override_nickname: '',
      user_override_avatar_url: '',
      user_override_gender: '',
      is_default_protagonist: false,
      is_default_antagonist: false,
      is_main_agent: false,
      is_active: true,
    };
    setFormData(initialData);
    setRoleConflict(null);
    setPendingSave(null);
  }, [agent, open]);

  const checkRoleConflict = async (roleType) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/check-role-conflict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role_type: roleType,
          current_agent_id: agent?.id || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.conflicting_agent || null;
      }
    } catch (error) {
      console.error('Errore nel controllo conflitto ruoli:', error);
    }
    return null;
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && checked && 
        (name === 'is_main_agent' || name === 'is_default_protagonist' || name === 'is_default_antagonist')) {
      
      const conflictingAgent = await checkRoleConflict(name);
      
      if (conflictingAgent) {
        setRoleConflict({ agent: conflictingAgent, roleType: name });
        setPendingSave({ ...formData, [name]: checked });
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleRoleConflictConfirm = async () => {
    if (!roleConflict || !pendingSave) return;
    
    try {
      const token = await user.getIdToken();
      await fetch(`${BACKEND_URL}/api/agents/${roleConflict.agent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [roleConflict.roleType]: false })
      });
      
      setFormData(pendingSave);
      setRoleConflict(null);
      setPendingSave(null);
    } catch (error) {
      console.error('Errore nella gestione del conflitto di ruolo:', error);
    }
  };

  const handleRoleConflictCancel = () => {
    setRoleConflict(null);
    setPendingSave(null);
  };

  const handleSave = () => {
    onSave(formData);
  };

  const roleLabels = {
    is_main_agent: 'Agente Principale',
    is_default_protagonist: 'Protagonista',
    is_default_antagonist: 'Antagonista'
  };

  const RoleConflictWarning = () => (
    <Alert 
      severity="warning" 
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={handleRoleConflictCancel}>Annulla</Button>
          <Button size="small" variant="contained" color="warning" onClick={handleRoleConflictConfirm}>
            Sostituisci
          </Button>
        </Box>
      }
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Conflitto di Ruolo: {roleLabels[roleConflict?.roleType]}
      </Typography>
      <Typography variant="caption">
        L'agente <strong>"{roleConflict?.agent?.user_override_nickname || roleConflict?.agent?.ux_name}"</strong> 
        è già impostato come {roleLabels[roleConflict?.roleType]?.toLowerCase()}. 
        Vuoi sostituirlo con questo agente?
      </Typography>
    </Alert>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: primaryColor }}>
        {agent ? 'Modifica Agente' : 'Crea Nuovo Agente'}
      </DialogTitle>
      <DialogContent>
        {roleConflict && <RoleConflictWarning />}
        
        <Stack spacing={2} sx={{ pt: 1 }}>
          {agent && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: primaryColor, mb: 1 }}>
                <InfoIcon sx={{ mr: 1, fontSize: 'small', verticalAlign: 'middle' }} />
                Informazioni Agente
              </Typography>
              <Grid container spacing={2}>
                {agent.ux_key && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Chiave Agente"
                      value={agent.ux_key}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
                {agent.ux_name && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome Ufficiale"
                      value={agent.ux_name}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
                {agent.sys_agent_category && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Categoria"
                      value={agent.sys_agent_category}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
                {agent.sys_agent_collection && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Collezione"
                      value={agent.sys_agent_collection}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                )}
                {agent.ux_description && (
                  <Grid item xs={12}>
                    <TextField
                      label="Descrizione"
                      value={agent.ux_description}
                      fullWidth
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      multiline
                      rows={2}
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <TextField 
            name="sys_agent_blueprint_id" 
            label="Blueprint ID" 
            value={formData.sys_agent_blueprint_id || ''} 
            onChange={handleChange} 
            fullWidth 
            required 
            disabled={!!agent} 
          />
          <TextField 
            name="user_override_nickname" 
            label="Nickname Personalizzato" 
            value={formData.user_override_nickname || ''} 
            onChange={handleChange} 
            fullWidth 
          />
          <TextField 
            name="user_override_avatar_url" 
            label="URL Avatar Personalizzato" 
            value={formData.user_override_avatar_url || ''} 
            onChange={handleChange} 
            fullWidth 
          />
          <TextField 
            name="user_override_gender" 
            label="Genere Personalizzato" 
            value={formData.user_override_gender || ''} 
            onChange={handleChange} 
            fullWidth 
            helperText="Es. male, female" 
          />
          
          <FormControlLabel 
            control={
              <Switch 
                checked={formData.is_main_agent || false} 
                onChange={handleChange} 
                name="is_main_agent"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: primaryColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor },
                }}
              />
            } 
            label={
              <Box>
                <Typography>Agente Principale</Typography>
                <Typography variant="caption" color="text.secondary">
                  Solo un agente può essere principale alla volta
                </Typography>
              </Box>
            }
          />
          <FormControlLabel 
            control={
              <Switch 
                checked={formData.is_active !== false} 
                onChange={handleChange} 
                name="is_active"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: primaryColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor },
                }}
              />
            } 
            label="Attivo" 
          />
          <FormControlLabel 
            control={
              <Switch 
                checked={formData.is_default_protagonist || false} 
                onChange={handleChange} 
                name="is_default_protagonist"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: primaryColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor },
                }}
              />
            } 
            label={
              <Box>
                <Typography>Protagonista di Default</Typography>
                <Typography variant="caption" color="text.secondary">
                  Solo un agente può essere protagonista alla volta
                </Typography>
              </Box>
            }
          />
          <FormControlLabel 
            control={
              <Switch 
                checked={formData.is_default_antagonist || false} 
                onChange={handleChange} 
                name="is_default_antagonist"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: primaryColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor },
                }}
              />
            } 
            label={
              <Box>
                <Typography>Antagonista di Default</Typography>
                <Typography variant="caption" color="text.secondary">
                  Solo un agente può essere antagonista alla volta
                </Typography>
              </Box>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={!!roleConflict}
          sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: `${primaryColor}dd` } }}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog di conferma... (ActivateConfirmDialog, DeactivateConfirmDialog, DeleteConfirmDialog)
const ActivateConfirmDialog = ({ open, onClose, onConfirm, agentName }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ color: 'success.main' }}>
        <PlayArrowIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Conferma Riattivazione
      </DialogTitle>
      <DialogContent>
        <Typography>
          Sei sicuro di voler riattivare l'agente <strong>"{agentName}"</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          L'agente tornerà a essere disponibile per le interazioni.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="success"
          startIcon={<PlayArrowIcon />}
        >
          Riattiva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeactivateConfirmDialog = ({ open, onClose, onConfirm, agentName }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ color: 'warning.main' }}>
        <PauseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Conferma Disattivazione
      </DialogTitle>
      <DialogContent>
        <Typography>
          Sei sicuro di voler disattivare l'agente <strong>"{agentName}"</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          L'agente sarà nascosto dalle interazioni ma potrai riattivarlo in qualsiasi momento.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="warning"
          startIcon={<PauseIcon />}
        >
          Disattiva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({ open, onClose, onConfirm, agentName }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ color: 'error.main' }}>
        <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Conferma Eliminazione
      </DialogTitle>
      <DialogContent>
        <Typography>
          Sei sicuro di voler eliminare definitivamente l'agente <strong>"{agentName}"</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Questa azione non può essere annullata. Potrai sempre riaggiungere l'agente in seguito.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="error"
          startIcon={<DeleteIcon />}
        >
          Elimina
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProfilePage = () => {
  const { user, profile } = useAuth();
  const { projectName, brandName, organizationName, primaryColor } = useBranding();
  const { config: domainConfig } = useDomain();
  const visualTheme = useVisualTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [activeTab, setActiveTab] = useState(0);
  
  // States per Agenti
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentSelectionModalOpen, setAgentSelectionModalOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [agentToActivate, setAgentToActivate] = useState(null);
  const [agentToDeactivate, setAgentToDeactivate] = useState(null);
  const [showInactiveAgents, setShowInactiveAgents] = useState(true);

  // States per Rooms - NUOVO
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const [formData, setFormData] = useState({
    user_display_name: '',
    user_phone_number: '',
    user_birth_date: '',
    user_age_group: '',
    user_photo_url: ''
  });

  const themeColors = {
    primary: visualTheme?.secondary_colors?.accent || primaryColor || '#1976d2',
    background: visualTheme?.primary_colors?.background || '#f8f9fa',
    header: visualTheme?.primary_colors?.header || '#ffffff',
    textPrimary: visualTheme?.text_colors?.primary || '#212529',
    textSecondary: visualTheme?.text_colors?.secondary || '#6c757d',
    link: visualTheme?.secondary_colors?.link || '#0d6efd'
  };

  // Tabs configuration
  const TABS = [
    { label: "Informazioni", icon: <PersonIcon /> },
    { label: "Agenti", icon: <SmartToyIcon /> },
    { label: "Meetings", icon: <VideoCallIcon /> },
    { label: "Chats", icon: <ChatIcon /> },
    { label: "Cards", icon: <FolderIcon /> },
    { label: "Rooms", icon: <MeetingRoomIcon /> },
    { label: "Floors", icon: <ForumIcon /> }
  ];

  useEffect(() => {
    if (profile) {
      const profileData = {
        user_display_name: profile.user_display_name || '',
        user_phone_number: profile.user_phone_number || '',
        user_birth_date: profile.user_birth_date || '',
        user_age_group: profile.user_age_group || '',
        user_photo_url: profile.user_photo_url || ''
      };
      setFormData(profileData);
      setEditFormData(profileData);
      setLoading(false);
    }
  }, [profile]);

  // Fetch Agents - esistente
  const fetchAgents = useCallback(async () => {
    if (!user || !domainConfig) return;
    
    setLoadingAgents(true);
    try {
      const token = await user.getIdToken();
      const projectId = domainConfig.project_id;
      
      const url = new URL(`${BACKEND_URL}/api/agents`);
      if (projectId) {
        url.searchParams.append('project_id', projectId);
      }
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      } else {
        setFeedback({ type: 'error', message: 'Errore nel caricamento degli agenti.' });
      }
    } catch (error) {
      console.error('[ProfilePage] Errore nel caricamento degli agenti:', error);
      setFeedback({ type: 'error', message: 'Errore di connessione nel caricamento degli agenti.' });
    } finally {
      setLoadingAgents(false);
    }
  }, [user, domainConfig]);

  // Fetch Rooms - NUOVO
  const fetchRooms = useCallback(async () => {
    if (!user || !domainConfig) return;
    
    setLoadingRooms(true);
    try {
      const token = await user.getIdToken();
      const projectId = domainConfig.project_id;
      
      if (!projectId) {
        setRooms([]);
        return;
      }
      
      const url = new URL(`${BACKEND_URL}/api/rooms`);
      url.searchParams.append('project_id', projectId);
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      } else {
        setFeedback({ type: 'error', message: 'Errore nel caricamento delle room.' });
      }
    } catch (error) {
      console.error('[ProfilePage] Errore nel caricamento delle room:', error);
      setFeedback({ type: 'error', message: 'Errore di connessione nel caricamento delle room.' });
    } finally {
      setLoadingRooms(false);
    }
  }, [user, domainConfig]);

  useEffect(() => {
    if (activeTab === 1) { // Agents tab
      fetchAgents();
    } else if (activeTab === 5) { // Rooms tab
      fetchRooms();
    }
  }, [activeTab, fetchAgents, fetchRooms]);

  // Handlers esistenti per Agenti...
  const handleStartEdit = () => {
    setEditFormData({ ...formData });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditFormData({ ...formData });
    setIsEditing(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfileEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        setFormData({ ...editFormData });
        setIsEditing(false);
        setFeedback({ type: 'success', message: 'Profilo aggiornato con successo!' });
      } else {
        setFeedback({ type: 'error', message: 'Errore nell\'aggiornamento del profilo.' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAgent = async (agentData) => {
    try {
      const token = await user.getIdToken();
      const projectId = domainConfig?.project_id;
      
      const url = selectedAgent 
        ? `${BACKEND_URL}/api/agents/${selectedAgent.id}`
        : `${BACKEND_URL}/api/agents`;
      const method = selectedAgent ? 'PUT' : 'POST';

      const finalUrl = new URL(url);
      if (!selectedAgent && projectId) {
        finalUrl.searchParams.append('project_id', projectId);
      }

      const response = await fetch(finalUrl.toString(), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(agentData)
      });

      if (response.ok) {
        setAgentModalOpen(false);
        setSelectedAgent(null);
        fetchAgents();
        setFeedback({ 
          type: 'success', 
          message: selectedAgent ? 'Agente aggiornato con successo!' : 'Agente creato con successo!' 
        });
      } else {
        setFeedback({ type: 'error', message: 'Errore nel salvataggio dell\'agente.' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    }
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setAgentModalOpen(true);
  };

  const handleActivateAgent = (agent) => {
    setAgentToActivate(agent);
    setActivateConfirmOpen(true);
  };

  const handleDeactivateAgent = (agent) => {
    setAgentToDeactivate(agent);
    setDeactivateConfirmOpen(true);
  };

  const handleDeleteAgent = (agent) => {
    setAgentToDelete(agent);
    setDeleteConfirmOpen(true);
  };

  const confirmActivateAgent = async () => {
    if (!agentToActivate) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/${agentToActivate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...agentToActivate, is_active: true })
      });

      if (response.ok) {
        setActivateConfirmOpen(false);
        setAgentToActivate(null);
        fetchAgents();
        setFeedback({ 
          type: 'success', 
          message: `Agente "${agentToActivate.user_override_nickname || agentToActivate.ux_name || 'Senza Nome'}" riattivato con successo!` 
        });
      } else {
        const errorData = await response.json();
        setFeedback({ type: 'error', message: errorData.detail || 'Errore nella riattivazione dell\'agente.' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    }
  };

  const confirmDeactivateAgent = async () => {
    if (!agentToDeactivate) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/${agentToDeactivate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...agentToDeactivate, is_active: false })
      });

      if (response.ok) {
        setDeactivateConfirmOpen(false);
        setAgentToDeactivate(null);
        fetchAgents();
        setFeedback({ 
          type: 'success', 
          message: `Agente "${agentToDeactivate.user_override_nickname || agentToDeactivate.ux_name || 'Senza Nome'}" disattivato con successo!` 
        });
      } else {
        const errorData = await response.json();
        setFeedback({ type: 'error', message: errorData.detail || 'Errore nella disattivazione dell\'agente.' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    }
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/${agentToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setDeleteConfirmOpen(false);
        setAgentToDelete(null);
        fetchAgents();
        setFeedback({ 
          type: 'success', 
          message: `Agente "${agentToDelete.user_override_nickname || agentToDelete.ux_name || 'Senza Nome'}" eliminato con successo!` 
        });
      } else {
        const errorData = await response.json();
        setFeedback({ type: 'error', message: errorData.detail || 'Errore nell\'eliminazione dell\'agente.' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    }
  };

  const handleAddAgentFromProject = () => {
    setAgentSelectionModalOpen(true);
  };

  const handleAgentSelected = (agent) => {
    fetchAgents();
    setFeedback({ 
      type: 'success', 
      message: `Agente "${agent.ux_name || agent.pro_override_display_name}" aggiunto con successo!` 
    });
  };

  const isProjectAgent = (agent) => {
    return !agent.user_auth_provider_id || agent.user_auth_provider_id !== user?.uid;
  };

  // Handlers per Rooms - NUOVO
  const handleEnterRoom = async (room) => {
    try {
      const token = await user.getIdToken();
      const projectId = domainConfig?.project_id;
      
      const response = await fetch(`${BACKEND_URL}/api/rooms/${room.id}?project_id=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const roomData = await response.json();
        setFeedback({ 
          type: 'success', 
          message: `Accesso alla room "${room.name || 'Senza Nome'}" effettuato con successo!` 
        });
        // Qui puoi implementare la logica per "entrare" nella room
        // Ad esempio, aprire una nuova finestra, reindirizzare, ecc.
        console.log('Entering room:', roomData);
      } else {
        setFeedback({ type: 'error', message: 'Errore nell\'accesso alla room.' });
      }
    } catch (error) {
      console.error('Errore nell\'accesso alla room:', error);
      setFeedback({ type: 'error', message: 'Errore di connessione.' });
    }
  };

  const handleRefreshRooms = () => {
    fetchRooms();
  };

  // Filtro agenti per visualizzazione
  const filteredAgents = showInactiveAgents 
    ? agents 
    : agents.filter(agent => agent.is_active !== false);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={400} />
        </Stack>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Impossibile caricare il profilo utente.</Alert>
      </Container>
    );
  }

  const {
    user_email,
    is_email_verified,
    created_at,
    roles,
    user_account_status,
    id: userId
  } = profile;

  const roleChipConfig = {
    platform_admin: { icon: <AdminPanelSettingsIcon />, color: 'primary', label: 'Admin Piattaforma' },
    project_admin: { icon: <BusinessIcon />, color: 'secondary', label: 'Admin Progetto' },
    brand_admin: { icon: <StyleIcon />, color: 'info', label: 'Admin Brand' },
    organization_admin: { icon: <BusinessIcon />, color: 'warning', label: 'Admin Organizzazione' },
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case 'active':
        return { label: 'Attivo', color: 'success' };
      case 'suspended':
        return { label: 'Sospeso', color: 'error' };
      case 'pending_verification':
        return { label: 'In Verifica', color: 'warning' };
      default:
        return { label: status || 'Non Definito', color: 'default' };
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3, color: themeColors.primary }}>
        La mia Dashboard
      </Typography>

      {feedback.message && (
        <Alert 
          severity={feedback.type} 
          sx={{ mb: 3 }} 
          onClose={() => setFeedback({ type: '', message: '' })}
        >
          {feedback.message}
        </Alert>
      )}

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ 
          mb: 3, 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTab-root.Mui-selected': { color: themeColors.primary },
          '& .MuiTabs-indicator': { backgroundColor: themeColors.primary },
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((tab, index) => (
          <Tab 
            key={index}
            label={tab.label} 
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* Tab 0: Informazioni Personali */}
      {activeTab === 0 && (
        <Card elevation={2} sx={{ backgroundColor: themeColors.header }}>
          <CardHeader 
            title="Informazioni Personali" 
            titleTypographyProps={{ fontWeight: 600, color: themeColors.primary }}
            action={
              !isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStartEdit}
                  sx={{ 
                    borderColor: themeColors.primary, 
                    color: themeColors.primary,
                    '&:hover': { borderColor: themeColors.primary, backgroundColor: `${themeColors.primary}10` }
                  }}
                >
                  Modifica
                </Button>
              ) : null
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar 
                  src={isEditing ? editFormData.user_photo_url : formData.user_photo_url} 
                  sx={{ width: 80, height: 80, bgcolor: themeColors.primary }}
                >
                  {(isEditing ? editFormData.user_display_name : formData.user_display_name)?.charAt(0)}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="h5" fontWeight={600} sx={{ color: themeColors.textPrimary }}>
                    {isEditing ? editFormData.user_display_name || 'Nome non impostato' : formData.user_display_name || 'Nome non impostato'}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon color="action" fontSize="small" />
                    <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
                      {user_email}
                    </Typography>
                    {is_email_verified && (
                      <Tooltip title="Email verificata">
                        <VerifiedIcon color="success" fontSize="small" />
                      </Tooltip>
                    )}
                  </Stack>
                  
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {user_account_status && (
                      <Chip
                        label={getStatusChipProps(user_account_status).label}
                        color={getStatusChipProps(user_account_status).color}
                        size="small"
                        variant="filled"
                      />
                    )}

                    {roles && roles.map(role => {
                      const config = roleChipConfig[role];
                      if (!config) return null;
                      return (
                        <Chip
                          key={role}
                          icon={config.icon}
                          label={config.label}
                          size="small"
                          color={config.color}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="user_display_name"
                  label="Nome Visualizzato"
                  value={isEditing ? editFormData.user_display_name : formData.user_display_name}
                  onChange={isEditing ? handleEditInputChange : undefined}
                  fullWidth
                  variant="outlined"
                  InputProps={{ readOnly: !isEditing }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="user_phone_number"
                  label="Numero di Telefono"
                  value={isEditing ? editFormData.user_phone_number : formData.user_phone_number}
                  onChange={isEditing ? handleEditInputChange : undefined}
                  fullWidth
                  variant="outlined"
                  InputProps={{ readOnly: !isEditing }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="user_birth_date"
                  label="Data di Nascita"
                  type="date"
                  value={isEditing ? editFormData.user_birth_date : formData.user_birth_date}
                  onChange={isEditing ? handleEditInputChange : undefined}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditing }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="user_age_group"
                  label="Fascia d'Età"
                  value={isEditing ? editFormData.user_age_group : formData.user_age_group}
                  onChange={isEditing ? handleEditInputChange : undefined}
                  fullWidth
                  variant="outlined"
                  placeholder="Es. 25-34"
                  InputProps={{ readOnly: !isEditing }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="user_photo_url"
                  label="URL Foto Profilo"
                  value={isEditing ? editFormData.user_photo_url : formData.user_photo_url}
                  onChange={isEditing ? handleEditInputChange : undefined}
                  fullWidth
                  variant="outlined"
                  InputProps={{ readOnly: !isEditing }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '& .MuiInputLabel-root.Mui-focused': { color: themeColors.primary },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
          {isEditing && (
            <>
              <Divider />
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Annulla
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                  onClick={handleSaveProfileEdit}
                  sx={{ 
                    bgcolor: themeColors.primary, 
                    '&:hover': { bgcolor: `${themeColors.primary}dd` } 
                  }}
                >
                  {saving ? 'Salvando...' : 'Salva Modifiche'}
                </Button>
              </Box>
            </>
          )}
        </Card>
      )}

      {/* Tab 1: Agenti */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: themeColors.textPrimary }}>
              I Miei Agenti
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={showInactiveAgents}
                    onChange={(e) => setShowInactiveAgents(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: themeColors.primary },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: themeColors.primary },
                    }}
                  />
                }
                label="Mostra disattivati"
                sx={{ mr: 2 }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleAddAgentFromProject}
                sx={{ 
                  bgcolor: themeColors.primary, 
                  '&:hover': { bgcolor: `${themeColors.primary}dd` } 
                }}
              >
                Aggiungi Agente
              </Button>
            </Box>
          </Box>

          {!domainConfig?.project_id && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <DomainIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Nessun progetto configurato nel dominio. Verranno mostrati solo i tuoi agenti personali.
              </Typography>
            </Alert>
          )}

          {loadingAgents ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: themeColors.primary }} />
            </Box>
          ) : filteredAgents.length === 0 ? (
            <Card elevation={2} sx={{ backgroundColor: themeColors.header }}>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <SmartToyIcon sx={{ fontSize: 64, color: themeColors.textSecondary, mb: 2 }} />
                <Typography variant="h6" sx={{ color: themeColors.textSecondary }}>
                  {showInactiveAgents ? 'Nessun agente trovato' : 'Nessun agente attivo'}
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.textSecondary, mb: 3 }}>
                  {showInactiveAgents 
                    ? (domainConfig?.project_id 
                        ? 'Aggiungi il tuo primo agente dal progetto o verifica che il progetto abbia agenti abilitati.'
                        : 'Aggiungi il tuo primo agente personale.')
                    : 'Attiva la visualizzazione degli agenti disattivati per vedere tutti i tuoi agenti.'
                  }
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddAgentFromProject}
                  sx={{ 
                    bgcolor: themeColors.primary, 
                    '&:hover': { bgcolor: `${themeColors.primary}dd` } 
                  }}
                >
                  Aggiungi Primo Agente
                </Button>
              </CardContent>
            </Card>
          ) : (
            <List disablePadding>
              {filteredAgents.map((agent) => {
                const isActive = agent.is_active !== false;
                const isProjAgent = isProjectAgent(agent);
                
                return (
                  <ListItem 
                    key={agent.id}
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider',
                      borderRadius: 1, 
                      mb: 1,
                      backgroundColor: isActive ? themeColors.header : 'grey.50',
                      borderLeftWidth: isProjAgent ? 4 : 1,
                      borderLeftColor: isProjAgent ? themeColors.primary : 'divider',
                      opacity: isActive ? 1 : 0.7,
                      position: 'relative',
                      px: 2,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: isActive ? `${themeColors.primary}08` : 'grey.100'
                      }
                    }}
                    disablePadding
                  >
                    {!isActive && (
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bgcolor: 'warning.light', 
                        p: 0.5,
                        textAlign: 'center',
                        zIndex: 1,
                        borderRadius: '4px 4px 0 0'
                      }}>
                        <Typography variant="caption" fontWeight={600} color="warning.dark">
                          AGENTE DISATTIVATO
                        </Typography>
                      </Box>
                    )}
                    
                    <ListItemAvatar sx={{ mt: !isActive ? 2 : 0 }}>
                      <Avatar 
                        src={agent.user_override_avatar_url || agent.ux_avatar_url} 
                        sx={{ bgcolor: themeColors.primary, width: 48, height: 48 }}
                      >
                        {(agent.user_override_nickname || agent.ux_name || 'A').charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText 
                      sx={{ mt: !isActive ? 2 : 0, pr: 1 }}
                      primary={
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: themeColors.textPrimary }}>
                            {agent.user_override_nickname || agent.ux_name || 'Agente Senza Nome'}
                          </Typography>
                          
                          {/* ux_description piccolo in grigetto */}
                          {agent.ux_description && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block', 
                                lineHeight: 1.3,
                                mt: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              {agent.ux_description}
                            </Typography>
                          )}
                          
                          {/* ux_default_user_message piccolo in grigetto corsivo */}
                          {agent.ux_default_user_message && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block', 
                                fontStyle: 'italic', 
                                lineHeight: 1.3,
                                mt: 0.5,
                                fontSize: '0.7rem'
                              }}
                            >
                              "{agent.ux_default_user_message}"
                            </Typography>
                          )}
                          
                          {/* Chips per ruoli - solo i più importanti */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {isProjAgent && (
                              <Chip 
                                icon={<DomainIcon />}
                                label="Progetto" 
                                size="small" 
                                color="info" 
                                variant="filled"
                              />
                            )}
                            {agent.is_main_agent && (
                              <Chip 
                                icon={<StarIcon />}
                                label="Principale" 
                                size="small" 
                                color="primary" 
                                variant="filled"
                              />
                            )}
                            {agent.is_default_protagonist && (
                              <Chip 
                                label="Protagonista" 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                            {agent.is_default_antagonist && (
                              <Chip 
                                label="Antagonista" 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: !isActive ? 2 : 0 }}>
                      {isActive ? (
                        <>
                          <Tooltip title="Modifica agente">
                            <IconButton 
                              onClick={() => handleEditAgent(agent)}
                              size="small"
                              disabled={isProjAgent}
                              sx={{ 
                                color: themeColors.primary,
                                '&:disabled': { color: 'grey.300' }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Disattiva agente">
                            <IconButton 
                              onClick={() => handleDeactivateAgent(agent)}
                              size="small"
                              disabled={isProjAgent}
                              sx={{ 
                                color: 'warning.main',
                                '&:disabled': { color: 'grey.300' }
                              }}
                            >
                              <PauseIcon />
                            </IconButton>
                          </Tooltip>
                          {!isProjAgent && (
                            <Tooltip title="Elimina agente">
                              <IconButton 
                                onClick={() => handleDeleteAgent(agent)}
                                size="small"
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <>
                          <Tooltip title="Riattiva agente">
                            <IconButton 
                              onClick={() => handleActivateAgent(agent)}
                              size="small"
                              disabled={isProjAgent}
                              sx={{ 
                                color: 'success.main',
                                '&:disabled': { color: 'grey.300' }
                              }}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          </Tooltip>
                          {!isProjAgent && (
                            <Tooltip title="Elimina agente">
                              <IconButton 
                                onClick={() => handleDeleteAgent(agent)}
                                size="small"
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      )}

      {/* Tab 5: Rooms */}
      {activeTab === 5 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: themeColors.textPrimary }}>
              Le Mie Rooms
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshRooms}
              sx={{ 
                borderColor: themeColors.primary, 
                color: themeColors.primary,
                '&:hover': { borderColor: themeColors.primary, backgroundColor: `${themeColors.primary}10` }
              }}
            >
              Aggiorna
            </Button>
          </Box>

          {!domainConfig?.project_id && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <DomainIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Nessun progetto configurato nel dominio. Le rooms richiedono un progetto attivo.
              </Typography>
            </Alert>
          )}

          {loadingRooms ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: themeColors.primary }} />
            </Box>
          ) : rooms.length === 0 ? (
            <Card elevation={2} sx={{ backgroundColor: themeColors.header }}>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <MeetingRoomIcon sx={{ fontSize: 64, color: themeColors.textSecondary, mb: 2 }} />
                <Typography variant="h6" sx={{ color: themeColors.textSecondary }}>
                  Nessuna room trovata
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
                  {domainConfig?.project_id 
                    ? 'Non ci sono rooms disponibili per questo progetto.'
                    : 'Configura un progetto per accedere alle rooms.'
                  }
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {rooms.map((room) => (
                <Grid item xs={12} sm={6} md={4} key={room.id}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      backgroundColor: themeColors.header,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: `${themeColors.primary}08`,
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: themeColors.primary }}>
                          <MeetingRoomIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600} sx={{ color: themeColors.textPrimary }}>
                            {room.name || 'Room Senza Nome'}
                          </Typography>
                          {room.description && (
                            <Typography variant="caption" color="text.secondary">
                              {room.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {room.sys_room_category && (
                        <Chip 
                          label={room.sys_room_category} 
                          size="small" 
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                      )}
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        ID: {room.id?.substring(0, 8)}...
                      </Typography>
                      
                      {room.sys_room_collection && (
                        <Typography variant="caption" color="text.secondary">
                          Collezione: {room.sys_room_collection}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<LaunchIcon />}
                        onClick={() => handleEnterRoom(room)}
                        sx={{ 
                          bgcolor: themeColors.primary, 
                          '&:hover': { bgcolor: `${themeColors.primary}dd` } 
                        }}
                      >
                        Entra nella Room
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Placeholder per altre tabs */}
      {activeTab === 2 && (
        <MeetingsTab
          user={user}
          profile={profile}
          themeColors={themeColors}
          domainConfig={domainConfig}
          projectId={domainConfig?.project_id}
        />
      )}

      {activeTab === 3 && (
        <ChatsTab
          user={user}
          profile={profile}
          themeColors={themeColors}
          domainConfig={domainConfig}
          projectId={domainConfig?.project_id}
        />
      )}

      {activeTab === 4 && (
        <Card elevation={2} sx={{ backgroundColor: themeColors.header }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <FolderIcon sx={{ fontSize: 64, color: themeColors.textSecondary, mb: 2 }} />
            <Typography variant="h6" sx={{ color: themeColors.textSecondary }}>
              Cards
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
              Sezione in sviluppo
            </Typography>
          </CardContent>
        </Card>
      )}

      {activeTab === 6 && (
        <Card elevation={2} sx={{ backgroundColor: themeColors.header }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ForumIcon sx={{ fontSize: 64, color: themeColors.textSecondary, mb: 2 }} />
            <Typography variant="h6" sx={{ color: themeColors.textSecondary }}>
              Floors
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
              Sezione in sviluppo
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <AgentEditModal 
        open={agentModalOpen}
        onClose={() => {
          setAgentModalOpen(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
        onSave={handleSaveAgent}
        visualTheme={visualTheme}
      />

      <AgentSelectionModal 
        open={agentSelectionModalOpen}
        onClose={() => setAgentSelectionModalOpen(false)}
        onSelect={handleAgentSelected}
        primaryColor={themeColors.primary}
        projectId={domainConfig?.project_id}
      />

      <ActivateConfirmDialog 
        open={activateConfirmOpen}
        onClose={() => {
          setActivateConfirmOpen(false);
          setAgentToActivate(null);
        }}
        onConfirm={confirmActivateAgent}
        agentName={agentToActivate?.user_override_nickname || agentToActivate?.ux_name || 'Senza Nome'}
      />

      <DeactivateConfirmDialog 
        open={deactivateConfirmOpen}
        onClose={() => {
          setDeactivateConfirmOpen(false);
          setAgentToDeactivate(null);
        }}
        onConfirm={confirmDeactivateAgent}
        agentName={agentToDeactivate?.user_override_nickname || agentToDeactivate?.ux_name || 'Senza Nome'}
      />

      <DeleteConfirmDialog 
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setAgentToDelete(null);
        }}
        onConfirm={confirmDeleteAgent}
        agentName={agentToDelete?.user_override_nickname || agentToDelete?.ux_name || 'Senza Nome'}
      />
    </Container>
  );
};

export default ProfilePage;